/**
 * FREAK-ISH — Cross-Garment Design Selector
 * ==========================================
 * Fetches all products in a design family from the public
 * /collections/[design-slug]/products.json endpoint and renders a
 * garment-type pill selector above the variant picker.
 *
 * DATA SOURCE: Each `design:[slug]` tag has a matching Smart Collection
 * with handle = the bare slug (created in bulk on 2026-05-21). The
 * collections endpoint is public, same-origin, and returns up to 50
 * products per request — more than enough for any single design family
 * (largest today is "varsity-swing-team" at 7 garments).
 *
 * NO CONFIG REQUIRED. No Storefront API token, no freakishStoreConfig,
 * no theme.liquid setup. Just include the script:
 *
 *   {{ 'freakish-cross-garment.js' | asset_url | script_tag }}
 *
 * The snippet itself (snippets/freakish-cross-garment-selector.liquid)
 * is rendered by blocks/variant-picker.liquid above Horizon's native
 * variant picker on every PDP.
 *
 * GRACEFUL DEGRADATION:
 * - Single-garment designs (no matching collection): fetch 404s → hide.
 * - Collection exists but only contains current product: renderPills hides.
 * - Network or JSON parse error: caught, selector hidden.
 */

(function () {
  'use strict';

  // ── Garment type tag → human label + emoji mapping ──────────────────────
  const GARMENT_LABELS = {
    'type:crop-tank':    { label: 'Crop Tank',      icon: '🩱' },
    'type:unisex-tee':   { label: 'Unisex Tee',     icon: '👕' },
    'type:vneck':        { label: 'V-Neck',          icon: '👕' },
    'type:hat-dad':      { label: 'Dad Hat',         icon: '🧢' },
    'type:hat-trucker':  { label: 'Trucker Hat',     icon: '🧢' },
    'type:mens-tank':    { label: "Men's Tank",      icon: '🩲' },
    'type:muscle-tank':  { label: 'Muscle Tank',     icon: '🩲' },
    'type:crop-tee':     { label: 'Crop Tee',        icon: '👚' },
    'type:drinkware':    { label: 'Drinkware',       icon: '🥤' },
    'type:towel':        { label: 'Towel',           icon: '🏖️' },
    'type:tote':         { label: 'Tote Bag',        icon: '👜' },
    'type:weekender':    { label: 'Weekender Bag',   icon: '🧳' },
    'type:koozie':       { label: 'Koozie',          icon: '🍺' },
    'type:accessories':  { label: 'Accessories',     icon: '💍' },
  };

  /**
   * Fetch all products in the design-family collection.
   *
   * /collections/[handle]/products.json is the public Shopify storefront
   * endpoint. It returns products with a few field differences from the
   * Storefront API — most importantly `tags` is a comma-separated string,
   * not an array. We normalize to an array here so downstream code in
   * renderPills() can iterate tags uniformly.
   */
  async function fetchDesignFamily(designSlug) {
    try {
      const resp = await fetch(
        `/collections/${encodeURIComponent(designSlug)}/products.json?limit=50`,
        { headers: { Accept: 'application/json' } }
      );

      if (!resp.ok) {
        // 404 is expected for single-garment designs (no collection exists).
        // Anything else gets logged; the wrapper hides itself either way.
        if (resp.status !== 404) {
          console.error('[FREAK-ISH] Collection fetch failed:', resp.status);
        }
        return [];
      }

      const data = await resp.json();
      const products = Array.isArray(data?.products) ? data.products : [];

      // Normalize: `tags` arrives as a CSV string from this endpoint, but
      // renderPills expects an iterable of individual tags.
      return products.map((p) => ({
        ...p,
        tags: typeof p.tags === 'string'
          ? p.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : (p.tags || []),
      }));
    } catch (err) {
      console.error('[FREAK-ISH] Cross-garment fetch error:', err);
      return [];
    }
  }

  function getGarmentType(tags) {
    for (const tag of tags) {
      if (tag.startsWith('type:') && GARMENT_LABELS[tag]) {
        return { tag, ...GARMENT_LABELS[tag] };
      }
    }
    return null;
  }

  function renderPills(container, products, currentHandle) {
    // Build pills from sibling products only — the current product is
    // intentionally excluded. The label "Also available as" promises OTHER
    // garments; showing the current garment as an active pill contradicts
    // that promise.
    const seen = new Set();
    const pills = [];

    for (const product of products) {
      if (product.handle === currentHandle) continue;       // skip current
      const garment = getGarmentType(product.tags);
      if (!garment || seen.has(garment.tag)) continue;       // dedupe by type
      seen.add(garment.tag);
      pills.push({ product, garment });
    }

    // Sort alphabetically by garment label
    pills.sort((a, b) => a.garment.label.localeCompare(b.garment.label));

    if (pills.length === 0) {
      // No sibling garments — hide the selector entirely. Covers both the
      // single-garment-design case and the "current is the only product
      // matching the design slug" edge case.
      const wrapper = document.getElementById('freakish-garment-selector');
      if (wrapper) wrapper.style.display = 'none';
      return;
    }

    container.innerHTML = '';

    pills.forEach(({ product, garment }) => {
      // Every pill is a link to a sibling PDP. There's no "active" state
      // because the current product is filtered out above.
      const pill = document.createElement('a');
      pill.className = 'freakish-garment-pill';
      pill.setAttribute('role', 'listitem');
      pill.title = product.title;
      pill.href = `/products/${product.handle}`;

      pill.innerHTML = `
        <span class="freakish-garment-pill__icon" aria-hidden="true">${garment.icon}</span>
        ${garment.label}
      `;

      container.appendChild(pill);
    });
  }

  async function init() {
    const wrapper = document.getElementById('freakish-garment-selector');
    if (!wrapper) return;

    const designSlug = wrapper.dataset.designSlug;
    const currentHandle = wrapper.dataset.currentHandle;
    const pillsContainer = wrapper.querySelector('.freakish-garment-selector__pills');

    if (!designSlug || !pillsContainer) return;

    try {
      const products = await fetchDesignFamily(designSlug);
      renderPills(pillsContainer, products, currentHandle);
    } catch (err) {
      console.error('[FREAK-ISH] Cross-garment selector error:', err);
      wrapper.style.display = 'none';
    }
  }

  // Init on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
